import { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import { MY_ACTIVITY_PAGE } from 'constants/pages';
import {
  CreatePlayRequestPayload,
  CreatePlayRequestResponse,
  MAX_PLAY_REQUEST_MESSAGE_LENGTH,
  PLAY_REQUESTS_API,
} from 'constants/payloads/playRequests';
import { EventTypesEnum } from 'types/generated/client';
import { formatInVenueZone } from 'utils/shared/time/formatInVenueZone';
import { formatVenueTimeRange } from 'utils/shared/time/formatVenueTimeRange';
import { useApiGateway } from 'hooks/useApi';
import PaperPlane from 'svg/PaperPlane';
import type { FindGame } from 'screens/Find/types';
import ActivityBadge from 'components/ActivityBadge';
import { Button } from 'components/Button';
import Dot from 'components/Dot';
import Success3dIllustration from 'components/Find/Success3dIllustration';
import Modal from 'components/modals/Modal';
import CloseButton from './CloseButton';
import RemainingRequestsAlert from './RemainingRequestsAlert';
import RequestLimitModal from './RequestLimitModal';
import { useRemainingPlayRequests } from './useRemainingPlayRequests';

interface Props {
  game: FindGame | null;
  isOpen: boolean;
  /** Full close (X / backdrop / finish) — the detail modal does NOT reopen. */
  onClose: () => void;
  /** "Go back" — closes this modal so the detail modal underneath reopens. */
  onBack: () => void;
}

/** "•" separator between the date/time meta values (mirrors MatchSummary). */
/** The bordered game card shown in the modal: badge + title + date/time. */
function GameCard({ game }: { game: FindGame }) {
  const fmt = (date: string, pattern: string) => formatInVenueZone(date, pattern, game.timezone);

  return (
    <div className="flex w-full flex-col gap-ds-sm rounded-[1.25rem] border border-color-border-input-lightmode bg-color-bg-lightmode-primary p-ds-lg shadow-[0px_4px_10px_0px_rgba(0,0,0,0.16)] dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary">
      <ActivityBadge
        type={EventTypesEnum.OpenPlay}
        sport={game.sport}
        competitiveness={game.competitiveness}
        size="xs"
        sizeDesktop="sm"
      />
      <h3 className="typography-product-heading-mobile lg:typography-product-heading-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
        {game.title}
      </h3>
      <div className="typography-product-subheading-mobile lg:typography-product-subheading flex items-center gap-ds-xs text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
        <span>{fmt(game.startDateTime, 'MMM d')}</span>
        <Dot />
        <span>{fmt(game.startDateTime, 'EEE')}</span>
        <Dot />
        <span>{formatVenueTimeRange(game.startDateTime, game.endDateTime, game.timezone)}</span>
      </div>
    </div>
  );
}

/**
 * The "Request to play" flow for a game: compose an optional message and submit,
 * then a success confirmation. Rendered on top of the detail modal; on success it
 * refetches GetMyGameRequest so the detail modal's footer flips to "Your request".
 */
export default function RequestToPlayModal({ game, isOpen, onClose, onBack }: Props) {
  const router = useRouter();
  const apolloClient = useApolloClient();
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [message, setMessage] = useState('');
  const [remainingAfterSend, setRemainingAfterSend] = useState<number | null>(null);
  const { post, isLoading } = useApiGateway<CreatePlayRequestPayload, CreatePlayRequestResponse>(
    PLAY_REQUESTS_API,
  );
  const { remaining, refetch: refetchRemaining } = useRemainingPlayRequests({ skip: !isOpen });
  const isOutOfRequests = remaining === 0;

  // Safe to reset synchronously: Modal renders a snapshot of its children through
  // the exit transition, so the wiped form never shows on the way out.
  const reset = () => {
    setStep('form');
    setMessage('');
    setRemainingAfterSend(null);
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  const handleBack = () => {
    onBack();
    reset();
  };

  const handleSubmit = async () => {
    if (!game?.hostUserId) return;
    const response = await post({
      payload: {
        eventId: game.id,
        recipientUserId: game.hostUserId,
        requestMessage: message.trim() || undefined,
      },
    });
    if (response.isError) {
      toast.error(response.error || 'Could not send your request');
      return;
    }
    // Flip the detail modal underneath to its "Your request" state.
    apolloClient.refetchQueries({ include: ['GetMyGameRequest', 'GetFindGamesWithRoster'] });
    refetchRemaining();
    // The success step reads the server's count; the refetch above may still be in flight.
    const body = response.data as CreatePlayRequestResponse | undefined;
    setRemainingAfterSend(body?.requestsRemaining ?? null);
    setStep('success');
  };

  const handleGoToActivity = () => {
    handleClose();
    router.push(MY_ACTIVITY_PAGE);
  };

  // Nothing composed here could be sent, so the form is replaced outright — the
  // same stop the player flow shows. Back lands on the game, which is underneath.
  if (step === 'form' && isOutOfRequests) {
    return <RequestLimitModal isOpen={isOpen} onClose={handleBack} cancelLabel="Go back to game" />;
  }

  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      classNameMaxWidth="max-w-md lg:max-w-[40rem]"
      classNameRounded="rounded-t-3xl sm:rounded-2xl lg:rounded-3xl"
      classNameHeight="max-h-[90dvh]"
    >
      <div className="flex max-h-[90dvh] flex-col bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary">
        {step === 'form' ? (
          <>
            <header className="flex shrink-0 items-center justify-between border-b border-color-border-input-lightmode p-ds-lg dark:border-color-border-input-darkmode lg:px-ds-3xl lg:py-5">
              <h2 className="typography-product-heading-mobile lg:typography-product-heading-desktop text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                Request to play
              </h2>
              <CloseButton onClose={handleClose} />
            </header>

            <div className="flex min-h-0 flex-1 flex-col gap-ds-xl overflow-y-auto p-ds-lg lg:p-ds-3xl">
              <div className="flex flex-col items-center gap-ds-xl">
                {game && <GameCard game={game} />}
              </div>

              <RemainingRequestsAlert remaining={remaining} />

              <div className="flex flex-col gap-ds-md">
                <label
                  htmlFor="request-message"
                  className="typography-product-heading-compact-mobile lg:typography-product-heading-compact-desktop text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
                >
                  Say something (optional)
                </label>
                <textarea
                  id="request-message"
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder="Let's play!"
                  rows={3}
                  maxLength={MAX_PLAY_REQUEST_MESSAGE_LENGTH}
                  className="input-form"
                />
              </div>
            </div>

            {/* The desktop footer is the single submit action; the X is its only twin,
                so "Go back" stays a mobile affordance. */}
            <footer className="flex shrink-0 flex-col gap-ds-md border-t border-color-border-input-lightmode px-ds-lg pb-ds-lg pt-ds-lg dark:border-color-border-input-darkmode lg:border-t-0 lg:px-ds-xl lg:py-5">
              <Button variant="secondary" size="lg" onClick={handleBack} className="lg:hidden">
                Go back
              </Button>
              <Button
                variant="brand"
                size="lg"
                onClick={handleSubmit}
                loading={isLoading}
                disabled={!game?.hostUserId}
                iconRight={<PaperPlane className="h-5 w-5 shrink-0 max-lg:hidden" />}
              >
                Submit request
              </Button>
            </footer>
          </>
        ) : (
          <>
            <header className="flex shrink-0 items-center justify-end p-ds-lg lg:px-ds-3xl lg:py-5">
              <CloseButton onClose={handleClose} />
            </header>

            <div className="flex min-h-0 flex-1 flex-col items-center gap-ds-lg overflow-y-auto px-ds-lg pb-ds-2xl pt-ds-lg text-center lg:gap-ds-2xl lg:px-ds-3xl lg:pb-16">
              <Success3dIllustration className="h-[7.5rem] w-[7.5rem]" />
              {/* `contents` keeps the mobile column flat; a real text block at lg. */}
              <div className="contents lg:flex lg:w-full lg:flex-col lg:items-center lg:gap-ds-xl">
                <h2 className="typography-product-display text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  Done!
                </h2>
                <p className="typography-product-body max-w-[19.5rem] text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary lg:max-w-none">
                  All set. We&rsquo;ll notify you when the organizer checks your request. You can
                  manage your request in the &ldquo;Activity&rdquo; page.
                </p>
              </div>
              <RemainingRequestsAlert remaining={remainingAfterSend} className="w-full text-left" />
            </div>

            <footer className="flex shrink-0 flex-col gap-ds-md border-t border-color-border-input-lightmode px-ds-lg pb-ds-lg pt-ds-lg dark:border-color-border-input-darkmode lg:flex-row lg:gap-ds-xl lg:px-ds-3xl lg:py-5">
              <Button
                variant="secondary"
                size="lg"
                onClick={handleGoToActivity}
                className="lg:min-w-0 lg:flex-1"
              >
                View in <strong className="font-bold">Activity</strong>
              </Button>
              <Button
                variant="primary"
                size="lg"
                onClick={handleClose}
                className="lg:min-w-0 lg:flex-1"
              >
                Continue browsing
              </Button>
            </footer>
          </>
        )}
      </div>
    </Modal>
  );
}
